/**
 * The ruler readout. While the measure tool is active it shows the running total
 * and leg count, with Save / Clear / Done:
 *   - Save pins the current ruler (line + range circle) so it stays drawn while
 *     you do other things, then clears the active ruler and returns to pan.
 *   - Clear wipes the in-progress ruler (stays in the tool).
 *   - Done finishes without saving and returns to pan.
 * When there are pinned measurements a "Clear N saved" link removes them all
 * (individual ones are removed by tapping them on the chart).
 *
 * Note: Save replaced Undo, so a misplaced point mid-measure means Clear and
 * restart. Flagged for Edd — easy to re-add Undo alongside Save if missed.
 */
import { useChartPlotterStore } from '@/store';
import { useMeasureStore } from '@/store/measure';
import { useSavedMeasurementsStore } from '@/store/savedMeasurements';
import { useShellStore } from '@/store/shell';
import { measureTotalNm } from '@/map/measureLayer';
import { formatDistance } from '@/nav';
import { haptic } from '@/ui/haptics';

export function MeasureReadout() {
  const tool = useChartPlotterStore((s) => s.activeTool);
  const setActiveTool = useChartPlotterStore((s) => s.setActiveTool);
  const points = useMeasureStore((s) => s.points);
  const clear = useMeasureStore((s) => s.clear);
  const saveMeasurement = useSavedMeasurementsStore((s) => s.add);
  const savedCount = useSavedMeasurementsStore((s) => s.items.length);
  const clearSaved = useSavedMeasurementsStore((s) => s.clear);
  const showToast = useShellStore((s) => s.showToast);
  if (tool !== 'measure') return null;
  const total = measureTotalNm(points);
  const legs = Math.max(0, points.length - 1);
  const canSave = points.length >= 2;
  return (
    <div className="acp-measure-readout" role="group" aria-label="Ruler">
      <div className="mr-info">
        <b>Ruler</b>
        <span>
          {points.length === 0
            ? 'Tap the chart to lay the ruler'
            : `${formatDistance(total)} · ${legs} leg${legs === 1 ? '' : 's'}`}
        </span>
        {savedCount > 0 && (
          <button
            type="button"
            className="mr-clearsaved"
            onClick={() => {
              haptic('tick');
              clearSaved();
              showToast('Saved measurements cleared');
            }}
          >
            Clear {savedCount} saved
          </button>
        )}
      </div>
      <div className="mr-btns">
        <button
          type="button"
          className="acp-btn primary"
          disabled={!canSave}
          onClick={() => {
            haptic('commit');
            saveMeasurement(points);
            clear();
            setActiveTool('pan');
            showToast('Measurement saved');
          }}
        >
          Save
        </button>
        <button
          type="button"
          className="acp-btn"
          disabled={points.length === 0}
          onClick={() => {
            haptic('tick');
            clear();
          }}
        >
          Clear
        </button>
        <button
          type="button"
          className="acp-btn"
          onClick={() => {
            haptic('tick');
            clear();
            setActiveTool('pan');
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
